import React from 'react';
import { Truck, MapPin, User } from 'lucide-react';
import { Booking } from '../../types/booking';
interface DeliveryMethodProps {
  method: 'pickup' | 'dropoff' | null;
  onSelect: (method: 'pickup' | 'dropoff') => void;
  startDate?: string;
  existingBookings?: Booking[];
}
export function DeliveryMethod({
  method,
  onSelect,
  startDate,
  existingBookings = []
}: DeliveryMethodProps) {
  // Find who has the machine before the selected start date
  const getCurrentHolder = () => {
    if (!startDate || existingBookings.length === 0) return null;
    // Convert YYYY-MM-DD to timestamp for comparison
    const selectedStartTime = new Date(startDate).getTime();
    // Find the booking that ends closest to (but before) the selected start date
    const previousBooking = existingBookings.filter(booking => {
      // Skip completed bookings
      if (booking.status === 'completed') return false;
      // Convert DD/MM/YYYY to YYYY-MM-DD
      const [day, month, year] = booking.endDate.split('/');
      const bookingEndDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      const bookingEndTime = new Date(bookingEndDate).getTime();
      // Find bookings that end before the selected start date
      return bookingEndTime < selectedStartTime;
    }).sort((a, b) => {
      // Sort by end date descending (most recent first)
      const [dayA, monthA, yearA] = a.endDate.split('/');
      const [dayB, monthB, yearB] = b.endDate.split('/');
      const dateA = new Date(`${yearA}-${monthA.padStart(2, '0')}-${dayA.padStart(2, '0')}`).getTime();
      const dateB = new Date(`${yearB}-${monthB.padStart(2, '0')}-${dayB.padStart(2, '0')}`).getTime();
      return dateB - dateA;
    })[0];
    if (previousBooking) {
      return {
        name: previousBooking.userName,
        location: previousBooking.location || previousBooking.farm || 'Farm',
        available: true,
        email: previousBooking.email,
        phone: previousBooking.phone
      };
    }
    // No previous booking found - machine is at WMF
    return null;
  };
  const currentHolder = getCurrentHolder();
  return <div className="space-y-4" data-id="element-562">
      {currentHolder && <button onClick={() => onSelect('pickup')} className={`
            w-full p-6 rounded-xl border-2 text-left transition-all duration-200 flex items-start gap-4 group
            ${method === 'pickup' ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300 bg-white'}
          `} data-id="element-563">
          <div className={`
            p-3 rounded-lg transition-colors shrink-0
            ${method === 'pickup' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'}
          `} data-id="element-564">
            <Truck size={24} data-id="element-565" />
          </div>
          <div className="flex-1" data-id="element-566">
            <h4 className="font-bold text-slate-900 text-lg" data-id="element-567">Farm Pickup</h4>
            <p className="text-slate-500 mt-1 text-sm" data-id="element-568">
              Collect the machine from the current holder.
            </p>
            <div className="mt-3 pt-3 border-t border-slate-200 flex items-start gap-2" data-id="element-569">
              <User size={16} className="text-slate-400 mt-0.5 shrink-0" data-id="element-570" />
              <div className="text-sm" data-id="element-571">
                <p className="font-medium text-slate-700" data-id="element-572">
                  {currentHolder.name}
                </p>
                <p className="text-slate-500" data-id="element-573">{currentHolder.location}</p>
              </div>
            </div>
          </div>
        </button>}

      <button onClick={() => onSelect('dropoff')} className={`
          w-full p-6 rounded-xl border-2 text-left transition-all duration-200 flex items-start gap-4 group
          ${method === 'dropoff' ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300 bg-white'}
        `} data-id="element-574">
        <div className={`
          p-3 rounded-lg transition-colors shrink-0
          ${method === 'dropoff' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'}
        `} data-id="element-575">
          <MapPin size={24} data-id="element-576" />
        </div>
        <div data-id="element-577">
          <h4 className="font-bold text-slate-900 text-lg" data-id="element-578">
            Collection at Stanthorpe WMF
          </h4>
          <p className="text-slate-500 mt-1 text-sm" data-id="element-579">
            Pick up the machine from the Stanthorpe Waste Management Facility at
            your scheduled time.
          </p>
        </div>
      </button>

      {!currentHolder && <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700" data-id="element-580">
          <p className="font-medium" data-id="element-581">Machine Currently at WMF</p>
          <p className="opacity-80 mt-1" data-id="element-582">
            The machine is currently at Stanthorpe Waste Management Facility and
            available for collection.
          </p>
        </div>}
    </div>;
}